import { useEffect, useRef } from 'react'

/**
 * The one dismiss rule for every dropdown/popover menu in the app — click outside, or Escape,
 * closes it (WAI-ARIA menu-button pattern; Escape also returns focus to the trigger). Extracted
 * because `TopBar.tsx`'s two `Switcher`s and `AccountMenu.tsx` each independently reimplemented
 * `open`/`setOpen` with neither behaviour, so a menu stayed open until its own row was clicked —
 * closing only the calling menu, then, requires a shared rule new menus get for free instead of
 * a pattern every future one has to remember to copy.
 *
 * `pointerdown`, not `click` — it fires before the target's OWN `click` handler, so clicking a
 * SECOND menu's trigger closes the first (its target is outside the first menu's `containerRef`)
 * in the same tick the second one opens, without any cross-component "only one open" coordination:
 * two independent instances of this hook produce that behaviour by construction.
 */
export function useDismissableMenu<C extends HTMLElement = HTMLDivElement, T extends HTMLElement = HTMLButtonElement>(
  open: boolean,
  onClose: () => void,
) {
  const containerRef = useRef<C>(null)
  const triggerRef = useRef<T>(null)

  useEffect(() => {
    if (!open) return undefined

    function onPointerDown(e: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) onClose()
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      onClose()
      triggerRef.current?.focus()
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose])

  return { containerRef, triggerRef }
}
